/* eslint-disable no-magic-numbers */
import {
  Box3,
  Group,
  Object3D,
  Raycaster,
  Vector2,
} from '@technology/web3d-plugin-threejs';
import { Api } from '@technology/web3d/dist/esnext/Api.js';
import { FrustumHelper } from '@technology/web3d/dist/esnext/Helpers/FrustumHelper.js';
import { GenericModel } from '@technology/web3d/dist/esnext/GenericModel';
import { Caster } from '@technology/web3d/dist/esnext/Picker/Caster.js';
import { IIntersection } from '@technology/web3d/dist/esnext/Picker/IIntersection.js';
import { getLogger } from '@trimble/logging';
import { LRUCache } from 'lru-cache';
import proj4 from 'proj4';
import { FeaturesServiceSettings } from './config/features-service-settings';
import { FeaturesServiceNode } from './features-service-node';
import { FeaturesServiceTileCache } from './features-service-tile-cache';
import {
  FeatureMesh,
  FEATURE_MESH_USER_DATA_CLUSTER_KEY,
  FEATURE_MESH_USER_DATA_ID_KEY,
  GeoJsonFeature,
  setFeatureMeshColor,
} from './expansion/features-buffer-geom-translator';
import { TileFetchFailureTracker } from './expansion/tile-fetch-failure-tracker';
import { ObjectPool } from './object-pool';
import { FeaturesServiceDecoder } from './processing/features-service-decoder';
import { approximateFeaturesServiceMeshDataSize } from './processing/features-service-factory';
import { PromiseQueue } from './processing/promise-queue';
import {
  EMPTY_TILE,
  FeaturesServiceMeshData,
} from './types/features-service-mesh-data';
import {
  FeaturesServiceFeatureEvent,
  FeaturesServiceFeatureEventHandler,
} from './types/feature-events';

const REFERENCE_SYSTEM = proj4.Proj('WGS84');
const UNASSIGNED_JOB_ID = '__unassigned__';
const POINT_PICK_THRESHOLD = 12;
const LINE_PICK_THRESHOLD = 16;
const FEATURE_PICK_PRIORITY = 8;

const buildJobGroupKey = (layerId: string | undefined, jobId: string): string =>
  `${layerId ?? ''}::${jobId}`;

const parseJobIdFromGroupKey = (key: string): string => {
  const separatorIndex = key.indexOf('::');
  return separatorIndex === -1 ? key : key.slice(separatorIndex + 2);
};

const resolveJobId = (metadata: FeatureMesh['metadata']): string => {
  const jobId = metadata?.['common_jobId'];
  return typeof jobId === 'string' ? jobId : UNASSIGNED_JOB_ID;
};

export class FeaturesServiceModel extends GenericModel {
  private readonly logger = getLogger();
  private disposed = false;
  private readonly tileTreeRoots: Array<FeaturesServiceNode>;

  private readonly tileDataCache: LRUCache<number, FeaturesServiceMeshData>;
  private readonly meshDecoder: FeaturesServiceDecoder;

  private readonly intervalHandle: ReturnType<typeof setInterval> | null = null;

  private readonly raycaster = new Raycaster();
  private readonly mouse = new Vector2();
  // TODO: Implement wrapping so that long running sessions do not overflow the frameNumber
  private frameNumber = 0;
  // private frameStart = 0;
  private readonly tileCache: FeaturesServiceTileCache;
  private renderInProgress = false;
  // One Group per layer ID, each added directly to root.
  // Toggling group.visible controls the whole layer in O(1), and newly loaded
  // tiles added to the group automatically inherit its current visibility state.
  private readonly layerGroups = new Map<string, Group>();
  private readonly jobGroups = new Map<string, Group>();
  private readonly jobVisibility = new Map<string, boolean>();
  private readonly objectPool = new ObjectPool();
  // This flag is used to ensure that some housekeeping tasks are done
  // only once, after the first tiles have been rendered.
  private firstTilesRendered = false;

  private readonly meshDataPromiseQueue =
    new PromiseQueue<FeaturesServiceMeshData>(16, 'features-service-mesh-data');

  /** Shared across the tile tree: circuit-breaks + rate-limits fetch error spam. */
  private readonly fetchFailureTracker = new TileFetchFailureTracker();

  constructor(
    private readonly mapSettings: FeaturesServiceSettings,
    private readonly api: Api
  ) {
    super(mapSettings.options.modelId, api);
    if (!this.api.camera) {
      throw new Error('FeaturesServiceModel: api.camera is not defined');
    }

    this.tileDataCache = this.createTileDataCache();
    this.tileTreeRoots = [];
    this.tileCache = new FeaturesServiceTileCache();

    // Create a tileTreeRoot for each tile adjacent to the root tile.
    // This is necessary to ensure that the sites that straddle the border
    // of the root tile can also be viewed in their entirety.
    const rootCoordinate = this.mapSettings.options.tileOptions.rootCoordinate;
    // const _maxCoordinateAtZoom =
    //   Math.pow(2, this.mapSettings.options.tileOptions.rootZoomLevel) - 1;

    this.meshDecoder = new FeaturesServiceDecoder(
      this.mapSettings.options.tileFetcher.fetcher
    );

    const tileTreeRoot = new FeaturesServiceNode(
      api,
      mapSettings,
      this.root,
      this.mapSettings.options.tileOptions.rootZoomLevel,
      rootCoordinate,
      null,
      this.tileDataCache,
      this.meshDecoder,
      this.objectPool,
      this.meshDataPromiseQueue,
      this.fetchFailureTracker
    );

    this.tileTreeRoots.push(tileTreeRoot);

    // for (let x = -1; x <= 1; x++) {
    //   for (let y = -1; y <= 1; y++) {
    //     const coordinate = new Vector2(
    //       rootCoordinate.x + x,
    //       rootCoordinate.y + y
    //     );
    //
    //     // Wrap coordinates to ensure they stay within the valid range.
    //     // This would cause the tiles to wrap around the world if the edge is reached.
    //     if (coordinate.x < 0) coordinate.x = maxCoordinateAtZoom;
    //     if (coordinate.y < 0) coordinate.y = maxCoordinateAtZoom;
    //     if (coordinate.x > maxCoordinateAtZoom) coordinate.x = 0;
    //     if (coordinate.y > maxCoordinateAtZoom) coordinate.y = 0;
    //
    //     const tileTreeRoot = new FeaturesServiceNode(
    //       api,
    //       mapSettings,
    //       this.root,
    //       this.mapSettings.options.tileOptions.rootZoomLevel,
    //       coordinate,
    //       null,
    //       this.tileDataCache,
    //       this.meshDecoder,
    //       this.objectPool,
    //       this.textureQueues,
    //       this.meshDataPromiseQueue
    //     );
    //     this.tileTreeRoots.push(tileTreeRoot);
    //   }
    // }

    this.animate(this.lastUpdateTime);
    // This is used for stepping through the rendering process while debugging.
    // window.addEventListener('mouseup', (evt) => {
    //   if (evt.button === 2) {
    //     console.log('Setting renderScene on right mouse up');
    //     this.renderScene();
    //   }
    // });

    if (this.mapSettings.debug) {
      window.addEventListener('click', this.debugHandleMouseClick);
      // setInterval(() => {
      //   for (const [key, queue] of this.textureQueues) {
      //     getLogger().info(
      //       `Texture Queue [${key}] length, activeLength: `,
      //       queue.length,
      //       queue.activeLength
      //     );
      //     getLogger().info(
      //       `QM Data Queue length, activeLength: `,
      //       this.meshDataPromiseQueue.length,
      //       this.meshDataPromiseQueue.activeLength
      //     );
      //     getLogger().info('tree sizes: ');
      //     this.tileTreeRoots.forEach((r) => {
      //       const count = r.countTilesInTree();
      //       if (count > 20)
      //         getLogger().info(
      //           `Root Tile ${r.tileId}, total tiles in tree: ${count}`
      //         );
      //     });
      //   }
      // }, 5000);
    }
  }

  private lastUpdateTime = 0;

  private readonly fps = 30;
  private readonly interval = 1000 / this.fps;

  private animate(currentTime: number): void {
    if (this.disposed) {
      return;
    }
    requestAnimationFrame(this.animate.bind(this));
    const elapsed = currentTime - this.lastUpdateTime;

    if (elapsed < this.interval) {
      return;
    }

    // Update the bounding box every 30 frames (approximately once per second at 30fps)
    if (this.frameNumber % 30 === 0) {
      this.updateBoundingBox();
    }

    this.lastUpdateTime = currentTime - (elapsed % this.interval);

    this.renderScene();
    this.frameNumber++;
    // 2^24, max value that can be stored as a 32-bit float with full precision
    // We don't currently send the frame number to the GPU, but this value
    // is compatible with doing that in the future.
    const maxFrameNumber = 16_777_216;
    if (this.frameNumber === maxFrameNumber) {
      this.frameNumber = 0;
    }
  }

  private renderedTiles = new Set<FeaturesServiceNode>();

  // Tracks how many currently-rendered tiles contain each feature so that
  // shared features (duplicated across tile boundaries) are only present
  // once in the scene graph.
  private readonly renderedFeatureIds = new Map<string, number>();
  private readonly visibleFeatureMeshes = new Map<string, FeatureMesh>();
  private readonly clusterFeatureIds = new Set<string>();
  private readonly featureDataById = new Map<string, GeoJsonFeature>();
  private readonly selectedFeatureIds = new Set<string>();

  private readonly featureEventHandlers =
    new Set<FeaturesServiceFeatureEventHandler>();

  public addFeatureEventListener(
    handler: FeaturesServiceFeatureEventHandler
  ): () => void {
    this.featureEventHandlers.add(handler);
    return () => {
      this.featureEventHandlers.delete(handler);
    };
  }

  private emitFeatureEvent(event: FeaturesServiceFeatureEvent): void {
    this.featureEventHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        this.logger.error('Feature event handler failed', error);
      }
    });
  }

  private isClusterMesh(featureMesh: FeatureMesh): boolean {
    return featureMesh.metadata?.['cluster'] === true;
  }

  private tileLoadingSuspended = false;

  /**
   * Cancels in-flight tile fetches while feature data is hidden. Loaded tile
   * meshes and decoded cache entries are kept so visibility can be restored
   * without refetching the whole pyramid.
   */
  private suspendTileLoading(): void {
    if (this.tileLoadingSuspended) {
      return;
    }

    this.tileLoadingSuspended = true;
    this.meshDataPromiseQueue.clear();
    this.resetLoadingTiles();
  }

  /** Returns loading tiles to `start` after the fetch queue was cleared. */
  private resetLoadingTiles(): void {
    for (const root of this.tileTreeRoots) {
      root.traverseAll((node) => {
        node.cancelLoading();
      });
    }
  }

  private resumeTileLoading(): void {
    this.tileLoadingSuspended = false;
  }
  private registerVisibleFeatureMesh(featureMesh: FeatureMesh): void {
    if (this.isClusterMesh(featureMesh)) {
      return;
    }
    this.visibleFeatureMeshes.set(featureMesh.featureId, featureMesh);
    if (featureMesh.feature) {
      this.featureDataById.set(featureMesh.featureId, featureMesh.feature);
    }
    if (this.selectedFeatureIds.has(featureMesh.featureId)) {
      this.syncViewerSelectionForFeatureIds([...this.selectedFeatureIds]);
    }
  }

  private registerClusterFeatureMesh(featureMesh: FeatureMesh): void {
    this.clusterFeatureIds.add(featureMesh.featureId);
    if (featureMesh.feature) {
      this.featureDataById.set(featureMesh.featureId, featureMesh.feature);
    }
  }

  private unregisterClusterFeatureMesh(featureId: string): void {
    this.clusterFeatureIds.delete(featureId);
    this.featureDataById.delete(featureId);
    if (this.selectedFeatureIds.has(featureId)) {
      this.removeSelectedFeatureIds([featureId]);
    }
  }

  private unregisterVisibleFeatureMesh(featureId: string): void {
    this.visibleFeatureMeshes.delete(featureId);
    const isSelected = this.selectedFeatureIds.has(featureId);
    if (isSelected) {
      this.syncViewerSelectionForFeatureIds([...this.selectedFeatureIds]);
    } else {
      this.featureDataById.delete(featureId);
    }
  }

  private resolveFeatureIdFromObject(object: Object3D): string | undefined {
    let current: Object3D | null = object;
    while (current) {
      const featureId = current.userData[FEATURE_MESH_USER_DATA_ID_KEY];
      if (typeof featureId === 'string' && featureId.length > 0) {
        return featureId;
      }
      current = current.parent;
    }
    return undefined;
  }

  private objectIsCluster(object: Object3D): boolean {
    let current: Object3D | null = object;
    while (current) {
      if (current.userData[FEATURE_MESH_USER_DATA_CLUSTER_KEY] === true) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  private isClusterFeatureId(featureId: string): boolean {
    if (this.clusterFeatureIds.has(featureId)) {
      return true;
    }

    const registered = this.visibleFeatureMeshes.get(featureId);
    if (registered && this.isClusterMesh(registered)) {
      return true;
    }

    const cached = this.featureDataById.get(featureId);
    return cached?.metadata?.['cluster'] === true;
  }

  private isSelectableFeatureId(featureId: string): boolean {
    return (
      !this.isClusterFeatureId(featureId) &&
      this.isFeatureIdJobVisible(featureId)
    );
  }

  private resolveSelectableFeatureIdFromObject(
    object: Object3D
  ): string | undefined {
    if (this.objectIsCluster(object)) {
      return undefined;
    }

    const featureId = this.resolveFeatureIdFromObject(object);
    if (featureId === undefined) {
      return undefined;
    }

    if (!this.isSelectableFeatureId(featureId)) {
      return undefined;
    }

    const registered = this.visibleFeatureMeshes.get(featureId);
    const cached = this.featureDataById.get(featureId);
    if (!registered && !cached) {
      return undefined;
    }

    return featureId;
  }

  private isFeatureMeshJobVisible(featureMesh: FeatureMesh): boolean {
    if (!this.isJobVisibilityEnabled) {
      return true;
    }

    const jobId = resolveJobId(featureMesh.metadata);
    return this.jobVisibility.get(jobId) ?? false;
  }

  private isFeatureIdJobVisible(featureId: string): boolean {
    if (!this.isJobVisibilityEnabled) {
      return true;
    }

    const registered = this.visibleFeatureMeshes.get(featureId);
    if (registered) {
      return this.isFeatureMeshJobVisible(registered);
    }

    const cached = this.featureDataById.get(featureId);
    if (cached) {
      const jobId = resolveJobId(cached.metadata);
      return this.jobVisibility.get(jobId) ?? false;
    }

    return false;
  }

  public isJobVisible(jobId: string): boolean {
    if (!this.isJobVisibilityEnabled) {
      return true;
    }

    return this.jobVisibility.get(jobId) ?? false;
  }

  private getPickThreshold(): number {
    const snapDistance = this.api.settingsDispatcher.settings.snapDistance;
    if (typeof snapDistance === 'number' && snapDistance > 0) {
      return Math.max(LINE_PICK_THRESHOLD, snapDistance);
    }
    return LINE_PICK_THRESHOLD;
  }

  private applyPickThresholds(caster: Raycaster): void {
    const threshold = this.getPickThreshold();
    caster.params.Points = {
      ...(caster.params.Points ?? {}),
      threshold: Math.max(POINT_PICK_THRESHOLD, threshold),
    };
    caster.params.Line = {
      ...(caster.params.Line ?? {}),
      threshold,
    };
  }

  private collectPickableObjectIds(
    object: Object3D,
    objectIds: number[]
  ): void {
    object.traverse((obj) => {
      if ((obj as { geometry?: unknown }).geometry) {
        objectIds.push(obj.id);
      }
    });
  }

  public override pick(caster: Caster): Promise<IIntersection> | undefined {
    this.applyPickThresholds(caster);
    const pickResult = super.pick(caster);
    if (!pickResult) {
      return undefined;
    }

    return pickResult.then((intersection) => {
      if (!intersection) {
        return intersection;
      }
      intersection.pickPriority = FEATURE_PICK_PRIORITY;
      return intersection;
    });
  }

  public override areaPick(
    caster: Caster,
    containedOnly: boolean
  ): Promise<IIntersection> | undefined {
    if (!caster.frustum) {
      return undefined;
    }

    const objectIds: number[] = [];
    const padding = this.getPickThreshold();

    for (const featureMesh of this.visibleFeatureMeshes.values()) {
      if (this.isClusterMesh(featureMesh)) {
        continue;
      }

      if (!this.isFeatureMeshJobVisible(featureMesh)) {
        continue;
      }

      featureMesh.mesh.updateMatrixWorld(true);
      const boundingBox = new Box3().setFromObject(featureMesh.mesh);
      if (boundingBox.isEmpty()) {
        continue;
      }
      boundingBox.expandByScalar(padding);

      const matches = containedOnly
        ? FrustumHelper.planesContainBox(caster.frustum.planes, boundingBox)
        : FrustumHelper.planesIntersectBox(caster.frustum.planes, boundingBox);

      if (!matches) {
        continue;
      }

      this.collectPickableObjectIds(featureMesh.mesh, objectIds);
    }

    if (objectIds.length === 0) {
      return undefined;
    }

    return Promise.resolve({
      id: objectIds[0],
      childrenIds: objectIds,
      object: this.root,
      model: this,
      caster,
    } as IIntersection);
  }

  /**
   * Raycasts visible feature geometry at client screen coordinates and returns
   * the topmost feature id, if any.
   */
  public pickFeature(clientX: number, clientY: number): string | undefined {
    if (!this.api.camera || this.disposed) {
      return undefined;
    }

    const container = this.api.container;
    const dimensions = container.getBoundingClientRect();
    if (container.clientWidth <= 0 || container.clientHeight <= 0) {
      return undefined;
    }

    this.mouse.x =
      ((clientX - dimensions.left) / container.clientWidth) * 2 - 1;
    this.mouse.y =
      -((clientY - dimensions.top) / container.clientHeight) * 2 + 1;
    this.applyPickThresholds(this.raycaster);
    this.raycaster.setFromCamera(this.mouse, this.api.camera);

    const intersects = this.raycaster.intersectObject(this.root, true);
    for (const hit of intersects) {
      const featureId = this.resolveSelectableFeatureIdFromObject(hit.object);
      if (featureId !== undefined) {
        return featureId;
      }
    }

    return undefined;
  }

  public getFeature(featureId: string): GeoJsonFeature | undefined {
    return (
      this.featureDataById.get(featureId) ??
      this.visibleFeatureMeshes.get(featureId)?.feature
    );
  }

  public getMeshIdsForFeatureIds(
    featureIds: readonly string[]
  ): readonly number[] {
    const meshIds: number[] = [];

    for (const featureId of featureIds) {
      const featureMesh = this.visibleFeatureMeshes.get(featureId);
      if (
        featureMesh &&
        !this.isClusterMesh(featureMesh) &&
        this.isSelectableFeatureId(featureId)
      ) {
        this.collectPickableObjectIds(featureMesh.mesh, meshIds);
      }
    }

    return [...new Set(meshIds)];
  }

  private areSameMeshIds(
    left: readonly number[],
    right: readonly number[]
  ): boolean {
    return (
      left.length === right.length &&
      left.every((id, index) => id === right[index])
    );
  }

  private syncViewerSelectionForFeatureIds(
    featureIds: readonly string[]
  ): void {
    const meshIds = this.getMeshIdsForFeatureIds(featureIds);
    const currentMeshIds = this.api.selection.get(this.modelId) ?? [];

    if (this.areSameMeshIds(currentMeshIds, meshIds)) {
      return;
    }

    if (meshIds.length === 0) {
      if (this.api.selection.has(this.modelId)) {
        this.api.selection.delete(this.modelId);
      }
      return;
    }

    this.api.selection.set(this.modelId, [...meshIds]);
  }

  /**
   * Maps Web3D selection mesh ids (from click or area selection) back to
   * feature ids, skipping cluster aggregates.
   */
  public resolveFeatureIdsFromMeshIds(
    meshIds: readonly number[]
  ): readonly string[] {
    const featureIds = new Set<string>();

    for (const meshId of meshIds) {
      const object = this.root.getObjectById(meshId);
      if (!object) {
        continue;
      }

      const featureId = this.resolveSelectableFeatureIdFromObject(object);
      if (featureId === undefined) {
        continue;
      }

      featureIds.add(featureId);
    }

    return [...featureIds];
  }

  public getSelectedFeatureIds(): readonly string[] {
    return [...this.selectedFeatureIds];
  }

  public setSelectedFeatureIds(featureIds: readonly string[]): void {
    const nextIds = new Set<string>();

    for (const featureId of featureIds) {
      if (this.isClusterFeatureId(featureId)) {
        continue;
      }

      if (!this.isFeatureIdJobVisible(featureId)) {
        continue;
      }

      if (
        this.visibleFeatureMeshes.has(featureId) ||
        this.featureDataById.has(featureId)
      ) {
        nextIds.add(featureId);
      }
    }

    const selectionUnchanged =
      nextIds.size === this.selectedFeatureIds.size &&
      [...nextIds].every((featureId) => this.selectedFeatureIds.has(featureId));

    if (!selectionUnchanged) {
      this.selectedFeatureIds.clear();
      for (const featureId of nextIds) {
        this.selectedFeatureIds.add(featureId);
      }
    }

    this.syncViewerSelectionForFeatureIds([...nextIds]);
  }

  private removeSelectedFeatureIds(featureIds: readonly string[]): void {
    if (featureIds.length === 0) {
      return;
    }

    const remainingIds = [...this.selectedFeatureIds].filter(
      (featureId) => !featureIds.includes(featureId)
    );

    if (remainingIds.length === this.selectedFeatureIds.size) {
      return;
    }

    this.setSelectedFeatureIds(remainingIds);
  }

  private renderScene(): void {
    if (this.renderInProgress) {
      this.logger.debug('render in progress, skipping this frame');
      return;
    }

    if (this.disposed) {
      return;
    }

    if (!this.root.visible) {
      this.suspendTileLoading();
      return;
    }

    this.resumeTileLoading();

    // console.log('textureQueue length', this.textureFetcherPromiseQueue.length);
    // console.log('qmQueue length', this.textureFetcherPromiseQueue.length);

    this.renderInProgress = true;
    // this.frameStart = performance.now();

    // Snapshot the meshes of every currently-rendered tile before update() is
    // called. update() may trigger unload() → removeMesh() → meshes = null on
    // tiles that leave the frustum. Without this snapshot, the tilesToRemove
    // loop below would see null meshes and skip decrementing renderedFeatureIds,
    // leaking stale counts that make re-appearing features permanently invisible.
    const priorMeshes = new Map<FeaturesServiceNode, FeatureMesh[]>();
    for (const tile of this.renderedTiles) {
      if (tile.meshes) {
        priorMeshes.set(tile, tile.meshes);
      }
    }

    this.tileTreeRoots.forEach((t) => {
      t.update(
        this.frameNumber,
        this.api.camera!.frustum,
        this.firstTilesRendered
      );
    });

    // for (const root of this.tileTreeRoots) {
    //   if (root.countTilesInTree() > 30) {
    //     console.log('active tree', root);
    //   }
    // }

    const renderableTiles: FeaturesServiceNode[] = [];
    this.tileTreeRoots.forEach((t) => {
      this.collectRenderableTiles(t, renderableTiles);
    });
    for (const tile of renderableTiles) {
      if (tile.state === 'ready') {
        tile.transitionTo('rendered');
      }
      if (!this.tileCache.get(tile.tileId)) {
        this.tileCache.add(tile);
      }
    }

    const newRenderableSet = new Set(renderableTiles);

    const tilesToAdd: FeaturesServiceNode[] = [];
    for (const tile of renderableTiles) {
      if (!this.renderedTiles.has(tile)) {
        tilesToAdd.push(tile);
      }
    }

    const tilesToRemove: FeaturesServiceNode[] = [];
    for (const tile of this.renderedTiles) {
      if (!newRenderableSet.has(tile)) {
        tilesToRemove.push(tile);
      }
    }

    this.renderedTiles = newRenderableSet;

    tilesToAdd.forEach((tile) => {
      tile.meshes?.forEach((featureMesh) => {
        const count = this.renderedFeatureIds.get(featureMesh.featureId) ?? 0;
        this.renderedFeatureIds.set(featureMesh.featureId, count + 1);
        if (count === 0) {
          if (this.isClusterMesh(featureMesh)) {
            this.registerClusterFeatureMesh(featureMesh);
          } else {
            // Emit only for real features (clusters are not editable) on first
            // appearance, so consumers can mirror them into the line-editing model.
            if (featureMesh.feature) {
              this.emitFeatureEvent({
                type: 'feature:ingested',
                feature: featureMesh.feature,
              });
            }
            this.registerVisibleFeatureMesh(featureMesh);
          }
          this.getMeshParent(featureMesh).add(featureMesh.mesh);
        }
      });
    });

    if (!this.firstTilesRendered && this.renderedTiles.size > 0) {
      this.firstTilesRendered = true;
      this.handleInitialRender();
    }

    tilesToRemove.forEach((tile) => {
      // Use the pre-update snapshot: tile.meshes may already be null because
      // unload() → removeMesh() ran during this frame's update() pass.
      const meshes = priorMeshes.get(tile) ?? tile.meshes;
      meshes?.forEach((featureMesh) => {
        const count = this.renderedFeatureIds.get(featureMesh.featureId) ?? 0;
        if (count <= 1) {
          this.renderedFeatureIds.delete(featureMesh.featureId);
          if (this.isClusterMesh(featureMesh)) {
            this.unregisterClusterFeatureMesh(featureMesh.featureId);
          } else {
            this.unregisterVisibleFeatureMesh(featureMesh.featureId);
            this.emitFeatureEvent({
              type: 'feature:removed',
              featureId: featureMesh.featureId,
            });
          }
          this.root.remove(featureMesh.mesh);
          featureMesh.mesh.removeFromParent();
        } else {
          this.renderedFeatureIds.set(featureMesh.featureId, count - 1);
          // removeMesh() disposes and detaches the mesh during update() before
          // this loop runs. When count > 1 another tile should keep the feature
          // visible, but that tile's mesh was never added to the scene because
          // the deduplication guard (count === 0) blocked it in tilesToAdd.
          // Find the first remaining rendered tile that owns this feature and
          // whose mesh has not yet been added to the scene, then add it.
          if (!featureMesh.mesh.parent) {
            for (const remainingTile of this.renderedTiles) {
              const replacement = remainingTile.meshes?.find(
                (m) => m.featureId === featureMesh.featureId
              );
              if (replacement && !replacement.mesh.parent) {
                this.getMeshParent(replacement).add(replacement.mesh);
                break;
              }
            }
          }
        }
      });
    });

    this.tileCache.prune(this.frameNumber);

    this.renderInProgress = false;
    this.objectPool.reset();
    // console.log(
    //   '======== frame done ========',
    //   performance.now() - this.frameStart
    // );
    // const root = this.tileTreeRoots.find((r) => r.isVisibleInFrame);

    // console.log('Total tiles in tree:', root?.countTilesInTree());
    this.api.renderingManager?.redraw();
  }

  private handleInitialRender(): void {
    this.updateBoundingBox();
  }

  private collectRenderableTiles(
    node: FeaturesServiceNode,
    collection: FeaturesServiceNode[]
  ): void {
    if (!node.isVisibleInFrame) return; // Simple visibility check

    // The core logic for seamless transition:
    // A node is renderable if it's in a renderable state (ready, rendered)
    // or if it's in the refining state, acting as a placeholder for its loading children.
    const isRenderable =
      node.state === 'ready' ||
      node.state === 'rendered' ||
      node.state === 'refining';

    if (isRenderable) {
      // This tile is the one to render for this area. Stop traversal down this branch.
      collection.push(node);
      return;
    }

    // If the node is fully refined, it's not rendered itself, but its children might be.
    if (node.state === 'refined') {
      for (const child of node.children || []) {
        this.collectRenderableTiles(child, collection);
      }
    }

    // Note: If a tile is still loading or processing, neither it nor its children are added.
    // This implies its ancestor (which must be in the refining state) was already added to the collection.
  }

  public override dispose(): void {
    this.disposed = true;
    this.meshDataPromiseQueue.clear();
    super.dispose();
    this.tileTreeRoots.forEach((r) => {
      r.dispose();
    });
    this.layerGroups.clear();
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
    }
    window.removeEventListener('click', this.debugHandleMouseClick);
  }

  private createTileDataCache(): LRUCache<number, FeaturesServiceMeshData> {
    return new LRUCache<number, FeaturesServiceMeshData>({
      maxSize: 1024 * 1024 * 200,
      sizeCalculation: (t: FeaturesServiceMeshData) => {
        // EMPTY_TILE sentinel has negligible size; count as 1 byte so it stays in the LRU.
        if (t === EMPTY_TILE) return 1;
        return approximateFeaturesServiceMeshDataSize(t);
      },
    });
  }

  public cartographicToPosition(p: Vector2, fromProjection?: string): Vector2 {
    return this.mapSettings.projectPoint(
      p,
      fromProjection ? proj4.Proj(fromProjection) : REFERENCE_SYSTEM,
      this.mapSettings.sceneProj,
      new Vector2()
    );
  }

  public positionToCartographic(p: Vector2, toProjection?: string): Vector2 {
    return this.mapSettings.projectPoint(
      p,
      this.mapSettings.sceneProj,
      toProjection ? proj4.Proj(toProjection) : REFERENCE_SYSTEM,
      new Vector2()
    );
  }

  /**
   * Returns the existing layer group for `layerId`, creating and registering it
   * (as a child of `root`) if this is the first time the layer is referenced.
   * Visibility set before any tiles load is preserved when tiles arrive.
   */
  private getOrCreateLayerGroup(layerId: string): Group {
    let group = this.layerGroups.get(layerId);
    if (!group) {
      group = new Group();
      this.layerGroups.set(layerId, group);
      this.root.add(group);
    }
    return group;
  }

  /**
   * Returns the existing job group for a layer/job pair, creating and registering
   * it under the layer group when first referenced.
   */
  private getOrCreateJobGroup(
    layerId: string | undefined,
    jobId: string
  ): Group {
    const key = buildJobGroupKey(layerId, jobId);
    let group = this.jobGroups.get(key);
    if (!group) {
      group = new Group();
      group.visible = this.jobVisibility.get(jobId) ?? false;
      this.jobGroups.set(key, group);
      const parent =
        layerId != null ? this.getOrCreateLayerGroup(layerId) : this.root;
      parent.add(group);
    }
    return group;
  }

  private get isJobVisibilityEnabled(): boolean {
    return this.mapSettings.options.jobVisibilityEnabled === true;
  }

  private getMeshParent(featureMesh: FeatureMesh): Object3D {
    const layerId = featureMesh.metadata?.['common_layerId'];

    if (!this.isJobVisibilityEnabled) {
      return typeof layerId === 'string'
        ? this.getOrCreateLayerGroup(layerId)
        : this.root;
    }

    const jobId = resolveJobId(featureMesh.metadata);
    return this.getOrCreateJobGroup(
      typeof layerId === 'string' ? layerId : undefined,
      jobId
    );
  }

  /**
   * Shows or hides all meshes that belong to the given survey job by toggling
   * every job group keyed to that job id across all layers.
   */
  public setJobVisible(jobId: string, visible: boolean): void {
    if (!this.isJobVisibilityEnabled) {
      return;
    }

    this.jobVisibility.set(jobId, visible);

    for (const [key, group] of this.jobGroups) {
      if (parseJobIdFromGroupKey(key) === jobId) {
        group.visible = visible;
      }
    }

    if (!visible) {
      this.removeSelectedFeaturesForJob(jobId);
    }

    this.api.renderingManager?.redraw();
  }

  private removeSelectedFeaturesForJob(jobId: string): void {
    const remainingIds = [...this.selectedFeatureIds].filter(
      (featureId) => !this.isFeatureInJob(featureId, jobId)
    );

    if (remainingIds.length === this.selectedFeatureIds.size) {
      return;
    }

    this.setSelectedFeatureIds(remainingIds);
  }

  private isFeatureInJob(featureId: string, jobId: string): boolean {
    const registered = this.visibleFeatureMeshes.get(featureId);
    if (registered) {
      return resolveJobId(registered.metadata) === jobId;
    }

    const cached = this.featureDataById.get(featureId);
    if (cached) {
      return resolveJobId(cached.metadata) === jobId;
    }

    return false;
  }

  /**
   * World-space bounds of currently loaded meshes for the given survey job.
   * Returns undefined when job grouping is off or no geometry has loaded yet.
   */
  public getJobBoundingBox(jobId: string): Box3 | undefined {
    if (!this.isJobVisibilityEnabled) {
      return undefined;
    }

    const boundingBox = new Box3();
    let hasBounds = false;

    for (const [key, group] of this.jobGroups) {
      if (parseJobIdFromGroupKey(key) !== jobId) {
        continue;
      }

      const groupBox = new Box3().setFromObject(group);
      if (groupBox.isEmpty()) {
        continue;
      }

      boundingBox.union(groupBox);
      hasBounds = true;
    }

    return hasBounds ? boundingBox : undefined;
  }

  /**
   * Clears cached tile mesh data and unloads the tile tree so the next render
   * cycle refetches tiles using the current fetcher filters (e.g. job ids).
   */
  public invalidateTileData(): void {
    if (this.disposed) {
      return;
    }

    this.meshDataPromiseQueue.clear();
    this.tileDataCache.clear();

    for (const tile of this.renderedTiles) {
      tile.meshes?.forEach((featureMesh) => {
        this.root.remove(featureMesh.mesh);
        featureMesh.mesh.removeFromParent();
        if (!this.isClusterMesh(featureMesh)) {
          this.emitFeatureEvent({
            type: 'feature:removed',
            featureId: featureMesh.featureId,
          });
        }
      });
    }

    this.renderedTiles.clear();
    this.renderedFeatureIds.clear();
    this.visibleFeatureMeshes.clear();
    this.clusterFeatureIds.clear();
    this.featureDataById.clear();
    this.selectedFeatureIds.clear();
    this.firstTilesRendered = false;

    for (const root of this.tileTreeRoots) {
      root.unload();
    }

    this.api.renderingManager?.redraw();
  }

  /**
   * Shows or hides all meshes that belong to the given layer by toggling the
   * layer's `Group.visible` flag. Newly loaded tiles are routed into this group
   * and automatically inherit the current visibility without extra bookkeeping.
   */
  public setLayerVisible(layerId: string, visible: boolean): void {
    this.getOrCreateLayerGroup(layerId).visible = visible;
    this.api.renderingManager?.redraw();
  }

  /**
   * Updates the color on all rendered meshes for the given layer and patches
   * the shared LayerItem template so newly loaded tiles inherit the new color.
   * `colorHex` should be a CSS hex color (e.g. `"#ff0000"` or `"ff0000"`).
   */
  public setLayerColor(layerId: string, colorHex: string): boolean {
    let applied = false;

    const layers = this.mapSettings.options.layerItems;
    if (layers) {
      const layer = layers.find((l) => l.id === layerId);
      if (layer) {
        layer.template = {
          ...(layer.template ?? {}),
          geometryColorHexRGB: colorHex.replace(/^#/, ''),
        };
        applied = true;
      }
    }

    // Traverse the layer group to recolor all its current meshes in one pass.
    const group = this.layerGroups.get(layerId);
    if (group) {
      setFeatureMeshColor(group, colorHex);
      applied = true;
    }

    this.api.renderingManager?.redraw();
    return applied;
  }

  private readonly debugHandleMouseClick = (event: MouseEvent): void => {
    // console.log('--------------------------');
    // console.log('Frame', this.frameNumber);
    // const root = this.tileTreeRoots.find((r) => r.isVisibleInFrame);
    // console.log('Visible root tile', root?.tileId);
    // const renderableTiles: FeaturesServiceNode[] = [];
    // this.collectRenderableTiles(root!, renderableTiles);
    //
    // this.tileCache.prune(this.frameNumber);
    //
    // console.log('Renderable tiles', renderableTiles);
    //
    // const staleTiles: Array<{
    //   id: string;
    //   lastTouched: string;
    //   state: string;
    // }> = [];
    //
    // root?.traverseAll((node: FeaturesServiceNode) => {
    //   if (
    //     node.lastTouchedFrame < this.frameNumber - 10 &&
    //     node.state !== 'start'
    //   ) {
    //     staleTiles.push({
    //       id: node.tileId,
    //       lastTouched: node.lastTouchedFrame.toString(),
    //       state: node.state,
    //     });
    //   }
    // });
    // console.log(
    //   'Stale tiles (not touched this frame):',
    //   JSON.stringify(staleTiles)
    // );
    // console.log('---------------------------');
    try {
      // Convert screen coordinates to Three.js normalized device coordinates (-1 to +1)
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      this.raycaster.setFromCamera(this.mouse, this.api.camera!);
      const intersects = this.raycaster.intersectObject(this.root);

      if (intersects.length > 0) {
        const tile = intersects[0].object;
        // Log the tile indices used to fetch it
        this.logger.debug('Clicked Tile:', tile.userData, tile);
      } else {
        this.logger.debug('No tile intersected at click position');
      }
    } catch (error) {
      console.error('Error during raycasting:', error);
    }
  };

  private updateBoundingBox(): void {
    const boundingBox = new Box3();

    // Use the actual rendered tile set for a tight, accurate bounding box.
    // Root-level tiles cover the entire dataset at coarse zoom; rendered tiles
    // are the real visible leaves at the current LOD.
    for (const tile of this.renderedTiles) {
      if (tile.meshes?.length) {
        boundingBox.union(tile.box);
      }
    }

    const minValues = [];

    if (this.meshDecoder.minHeight !== null) {
      minValues.push(this.meshDecoder.minHeight);
    }

    if (minValues.length !== 0) {
      boundingBox.min.z = Math.min(...minValues);
    }

    boundingBox.max.z = this.meshDecoder.maxHeight ?? boundingBox.max.z;

    this._boundingBox.next(boundingBox);
  }
}
